import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  Anchor, ChevronRight, ChevronLeft, X,
  Hash, Trophy, Calendar, Bell, Sparkles, Target
} from 'lucide-react';

// Onboarding context
const OnboardingContext = createContext(null);
export const useOnboarding = () => useContext(OnboardingContext);

// Tour steps - each step targets a specific element on the page
const TOUR_STEPS = [
  {
    id: 'welcome',
    type: 'modal',
    title: 'Bienvenue à bord !',
    description: 'Je vais te faire découvrir l\'Équipage Odys en quelques étapes. C\'est parti !',
    icon: Anchor,
  },
  {
    id: 'nav-channels',
    type: 'highlight',
    target: '[data-tour="nav-channels"]',
    title: 'Les canaux',
    description: 'Le cœur de la communauté ! Discute avec les autres membres par thème.',
    icon: Hash,
    position: 'bottom',
  },
  {
    id: 'nav-challenges',
    type: 'highlight',
    target: '[data-tour="nav-challenges"]',
    title: 'Les défis',
    description: 'Participe aux défis hebdomadaires pour gagner des XP et progresser.',
    icon: Target,
    position: 'bottom',
  },
  {
    id: 'nav-victories',
    type: 'highlight',
    target: '[data-tour="nav-victories"]',
    title: 'Les victoires',
    description: 'Célèbre tes réussites ! Première offre, signature, location...',
    icon: Trophy,
    position: 'bottom',
  },
  {
    id: 'nav-calendar',
    type: 'highlight',
    target: '[data-tour="nav-calendar"]',
    title: 'Le calendrier',
    description: 'Tous les événements : lives, Q&A, webinaires. Ne rate rien !',
    icon: Calendar,
    position: 'bottom',
  },
  {
    id: 'notifications',
    type: 'modal',
    title: 'Active les notifications',
    description: 'Pour ne rien rater : nouveaux messages, événements, réponses...',
    icon: Bell,
    action: 'notifications',
  },
  {
    id: 'complete',
    type: 'modal',
    title: 'Tu es prêt !',
    description: 'Tu as toutes les clés. Commence par te présenter dans le canal #général !',
    icon: Sparkles,
    action: 'complete',
  },
];

// Tooltip component that points to elements
const TourTooltip = ({ step, onNext, onPrev, onSkip, currentIndex, total, targetRect }) => {
  const Icon = step.icon;
  
  // Calculate position based on target element
  const getPosition = () => {
    if (!targetRect) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    
    const padding = 16;
    const tooltipWidth = 320;
    const tooltipHeight = 200;
    
    switch (step.position) {
      case 'right':
        return {
          top: Math.max(padding, Math.min(targetRect.top, window.innerHeight - tooltipHeight - padding)),
          left: Math.min(targetRect.right + padding, window.innerWidth - tooltipWidth - padding),
        };
      case 'left':
        return {
          top: Math.max(padding, Math.min(targetRect.top, window.innerHeight - tooltipHeight - padding)),
          left: Math.max(padding, targetRect.left - tooltipWidth - padding),
        };
      case 'bottom':
        return {
          top: Math.min(targetRect.bottom + padding, window.innerHeight - tooltipHeight - padding),
          left: Math.max(padding, Math.min(targetRect.left, window.innerWidth - tooltipWidth - padding)),
        };
      case 'top':
        return {
          top: Math.max(padding, targetRect.top - tooltipHeight - padding),
          left: Math.max(padding, Math.min(targetRect.left, window.innerWidth - tooltipWidth - padding)),
        };
      default:
        return {
          top: targetRect.bottom + padding,
          left: targetRect.left,
        };
    }
  };

  const position = getPosition();

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        zIndex: 10001,
        width: '320px',
        maxWidth: 'calc(100vw - 32px)',
      }}
      className="pointer-events-auto"
    >
      <div className="bg-card border border-primary/30 rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-primary/10 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
              <Icon className="w-4 h-4 text-primary" />
            </div>
            <span className="text-xs text-muted-foreground">
              {currentIndex + 1} / {total}
            </span>
          </div>
          <button
            onClick={onSkip}
            data-testid="onboarding-skip"
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Passer
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <h3 className="font-bold text-lg mb-2">{step.title}</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {step.description}
          </p>
        </div>

        {/* Actions */}
        <div className="px-4 pb-4 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={onPrev}
            disabled={currentIndex === 0}
            className={currentIndex === 0 ? 'invisible' : ''}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Retour
          </Button>
          <Button size="sm" onClick={onNext} className="bg-primary hover:bg-primary/90">
            {currentIndex === total - 1 ? 'Terminer' : 'Suivant'}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

// Modal for welcome/end screens
const TourModal = ({ step, onNext, onPrev, onSkip, currentIndex, total, onAction }) => {
  const Icon = step.icon;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onSkip} />
      
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative w-full max-w-md bg-card rounded-2xl shadow-2xl border border-border overflow-hidden"
      >
        {/* Close button */}
        <button
          onClick={onSkip}
          className="absolute top-4 right-4 p-2 text-muted-foreground hover:text-foreground transition-colors"
          data-testid="onboarding-close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="p-8 text-center">
          {step.id === 'welcome' ? (
            <div className="relative w-24 h-24 mx-auto mb-6">
              <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
                <circle cx="50" cy="50" r="46" fill="none" stroke="hsl(var(--gold))" strokeWidth="1" strokeDasharray="2 4" opacity="0.6" />
                <circle cx="50" cy="50" r="38" fill="none" stroke="hsl(var(--gold))" strokeWidth="0.8" opacity="0.35" />
              </svg>
              <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-white flex items-center justify-center"
                style={{
                  border: '1.5px solid hsl(var(--gold))',
                  boxShadow: '0 4px 16px rgba(40,28,18,0.18), inset 0 0 0 3px hsl(var(--paper))',
                  padding: '8px',
                }}
              >
                <img
                  src="/logo-192.png"
                  alt="Odys"
                  className="w-full h-full object-contain"
                  onError={(e) => { e.target.outerHTML = '<span style="font-family:Georgia,serif;font-weight:700;font-style:italic;color:hsl(var(--gold-dark));font-size:1.1rem">EO</span>'; }}
                />
              </div>
            </div>
          ) : (
            <div className="w-20 h-20 rounded-2xl bg-primary/20 flex items-center justify-center mx-auto mb-6">
              <Icon className="w-10 h-10 text-primary" />
            </div>
          )}

          <h2 className="text-2xl font-bold mb-3">{step.title}</h2>
          <p className="text-muted-foreground mb-6 leading-relaxed">
            {step.description}
          </p>

          {step.action === 'notifications' && (
            <Button
              variant="outline"
              onClick={() => {
                if ('Notification' in window) {
                  Notification.requestPermission();
                }
                onNext();
              }}
              className="w-full mb-3"
            >
              <Bell className="w-4 h-4 mr-2" />
              Activer les notifications
            </Button>
          )}

          {step.action === 'complete' ? (
            <Button onClick={onAction} className="w-full bg-primary hover:bg-primary/90">
              C'est parti !
              <Sparkles className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={onPrev}
                disabled={currentIndex === 0}
                className={currentIndex === 0 ? 'invisible' : ''}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Retour
              </Button>
              <span className="text-xs text-muted-foreground">
                {currentIndex + 1} / {total}
              </span>
              <Button onClick={onNext} className="bg-primary hover:bg-primary/90" data-testid="onboarding-next">
                {step.action === 'notifications' ? 'Passer' : 'Suivant'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Spotlight overlay for highlighted elements
const Spotlight = ({ targetRect }) => {
  if (!targetRect) return null;

  const padding = 8;
  const spotlightRect = {
    top: targetRect.top - padding,
    left: targetRect.left - padding,
    width: targetRect.width + padding * 2,
    height: targetRect.height + padding * 2,
  };

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none">
      <svg className="w-full h-full">
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={spotlightRect.left}
              y={spotlightRect.top}
              width={spotlightRect.width}
              height={spotlightRect.height}
              rx="12"
              fill="black"
            />
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.75)"
          mask="url(#spotlight-mask)"
        />
      </svg>
      
      {/* Highlight border */}
      <div
        className="absolute border-2 border-primary rounded-xl animate-pulse pointer-events-none"
        style={{
          top: spotlightRect.top,
          left: spotlightRect.left,
          width: spotlightRect.width,
          height: spotlightRect.height,
        }}
      />
    </div>
  );
};

// Main provider
export const OnboardingProvider = ({ children }) => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  // Load saved progress (et synchronisation serveur : ne plus revenir d'un appareil à l'autre)
  useEffect(() => {
    const completed = localStorage.getItem('onboarding_completed');
    if (completed) {
      setIsActive(false);
      return;
    }

    const savedStep = localStorage.getItem('onboarding_step');
    if (savedStep) {
      setCurrentStep(parseInt(savedStep));
    }

    let cancelled = false;
    api.get('/users/me/onboarding-status')
      .then((res) => {
        if (!cancelled && res.data?.completed) {
          localStorage.setItem('onboarding_completed', 'true');
          localStorage.removeItem('onboarding_step');
          setIsActive(false);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [api]);

  // Start onboarding on homepage
  useEffect(() => {
    if (!user) return;

    // Gate behind CGU acceptance — Article 50 AI Act requires the CGU modal
    // to be dismissable first.
    if (user.cgu_up_to_date === false) {
      setIsActive(false);
      return;
    }

    const completed = localStorage.getItem('onboarding_completed');
    if (completed) return;
    
    if (location.pathname === '/') {
      setIsActive(true);
    }
  }, [user, location.pathname]);

  // Find and highlight target element
  useEffect(() => {
    if (!isActive) return;
    
    const step = TOUR_STEPS[currentStep];
    if (step?.type !== 'highlight' || !step.target) {
      setTargetRect(null);
      return;
    }

    let attempts = 0;
    const maxAttempts = 20;
    let pollInterval = null;

    const findAndHighlight = () => {
      const element = document.querySelector(step.target);
      if (element) {
        // Found! Clear the polling
        if (pollInterval) clearInterval(pollInterval);
        
        // Scroll element into view
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // Wait for scroll to complete then set rect
        setTimeout(() => {
          const rect = element.getBoundingClientRect();
          setTargetRect({
            top: rect.top,
            left: rect.left,
            right: rect.right,
            bottom: rect.bottom,
            width: rect.width,
            height: rect.height,
          });
        }, 400);
        return true;
      }
      return false;
    };

    // Try immediately
    if (!findAndHighlight()) {
      // Poll until found or max attempts
      pollInterval = setInterval(() => {
        attempts++;
        if (findAndHighlight() || attempts >= maxAttempts) {
          clearInterval(pollInterval);
        }
      }, 200);
    }
    
    // Update position on scroll/resize
    const handleUpdate = () => {
      const element = document.querySelector(step.target);
      if (element) {
        const rect = element.getBoundingClientRect();
        setTargetRect({
          top: rect.top,
          left: rect.left,
          right: rect.right,
          bottom: rect.bottom,
          width: rect.width,
          height: rect.height,
        });
      }
    };
    
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);
    
    return () => {
      if (pollInterval) clearInterval(pollInterval);
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isActive, currentStep]);

  // Save progress
  useEffect(() => {
    if (isActive && currentStep > 0) {
      localStorage.setItem('onboarding_step', currentStep.toString());
    }
  }, [currentStep, isActive]);

  const handleNext = useCallback(() => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const handleSkip = useCallback(async () => {
    setIsActive(false);
    setTargetRect(null);
    localStorage.setItem('onboarding_completed', 'true');
    localStorage.removeItem('onboarding_step');
    
    try {
      await api.post('/users/me/complete-onboarding');
    } catch (e) {
      console.error('Error completing onboarding:', e);
    }
  }, [api]);

  const handleComplete = useCallback(async () => {
    await handleSkip();
    navigate('/channels');
  }, [handleSkip, navigate]);

  const startOnboarding = useCallback(() => {
    localStorage.removeItem('onboarding_completed');
    localStorage.removeItem('onboarding_step');
    setCurrentStep(0);
    setIsActive(true);
    navigate('/');
  }, [navigate]);

  const step = TOUR_STEPS[currentStep];

  const contextValue = {
    isActive,
    currentStep,
    startOnboarding,
  };

  return (
    <OnboardingContext.Provider value={contextValue}>
      {children}
      
      <AnimatePresence>
        {isActive && step && (
          <>
            {step.type === 'highlight' && targetRect && (
              <Spotlight targetRect={targetRect} />
            )}
            
            {step.type === 'modal' ? (
              <TourModal
                step={step}
                onNext={handleNext}
                onPrev={handlePrev}
                onSkip={handleSkip}
                onAction={handleComplete}
                currentIndex={currentStep}
                total={TOUR_STEPS.length}
              />
            ) : (
              targetRect && (
                <TourTooltip
                  step={step}
                  onNext={handleNext}
                  onPrev={handlePrev}
                  onSkip={handleSkip}
                  currentIndex={currentStep}
                  total={TOUR_STEPS.length}
                  targetRect={targetRect}
                />
              )
            )}
          </>
        )}
      </AnimatePresence>
    </OnboardingContext.Provider>
  );
};

// Placeholder for page tips (no longer needed but keep export for compatibility)
export const OnboardingPageTip = () => null;

export default OnboardingProvider;
